import {
  Button,
  CloseButton,
  Grid,
  Heading,
  IconAddLine,
  List,
  Modal,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useState } from "react";
import { CyclesService } from "../../../services/CyclesService";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { InputDate } from "../../InputDate";
import { Selector } from "../../Selectors/Selector";
import { CycleContext } from "../CyclesAndGoals";
import { CycleTypePanel } from "../CycleTypes/CycleTypePanel";
import { CycleGoalEditModal } from "../Goals/CycleGoalEditModal";
import GoalEntry from "../Goals/GoalEntry";

const noneOption = { id: "0", label: "None" };

export const CycleEditModal = ({ cycle, open, setOpen }) => {
  const { cycles, setCycles, cycleTypes } = useContext(CycleContext);
  const showNotification = useNotification();
  const [cycleName, setCycleName] = useState(cycle?.name || "");
  const [parentCycle, setParentCycle] = useState(cycle?.parent_cycle || {});
  const [cycleType, setCycleType] = useState(cycle?.type || {});
  const [cycleStartDate, setCycleStartDate] = useState(
    cycle?.start_date || new Date()
  );
  const [cycleEndDate, setCycleEndDate] = useState(
    cycle?.end_date || new Date()
  );
  const [goals, setGoals] = useState(cycle?.goals || []);

  const [submitting, setSubmitting] = useState(false);
  const [cycleTypePanelOpen, setCycleTypePanelOpen] = useState(false);
  const [goalPanelOpen, setGoalPanelOpen] = useState(false);

  async function handleCreateCycle() {
    try {
      const cycleObj = {
        name: cycleName,
        parent_cycle: parentCycle.id === "0" ? null : parentCycle,
        type: cycleType,
        start_date: cycleStartDate,
        end_date: cycleEndDate,
        goals: goals,
      };
      const newCycle = await CyclesService.createCycle(cycleObj);
      newCycle.label = newCycle.name;
      setCycles((prevCycles) =>
        [newCycle, ...prevCycles].sort(
          (a, b) => new Date(a.end_date) - new Date(b.end_date)
        )
      );
      setOpen(false);
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("cyclesAndGoals.cycle"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("cyclesAndGoals.cycle"),
        }),
        ERROR
      );
    }
  }

  async function handleUpdateCycle() {
    try {
      const cycleObj = {
        id: cycle.id,
        name: cycleName,
        parent_cycle: parentCycle,
        type: cycleType,
        start_date: cycleStartDate,
        end_date: cycleEndDate,
        goals: goals,
      };
      const updatedCycle = await CyclesService.updateCycle(cycleObj);
      updatedCycle.label = updatedCycle.name;
      setCycles((prevCycles) =>
        prevCycles
          .map((c) => (c.id === updatedCycle.id ? updatedCycle : c))
          .sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
      );
      setOpen(false);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("cyclesAndGoals.cycle"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("cyclesAndGoals.cycle"),
        }),
        ERROR
      );
    }
  }

  function isNotComplete() {
    if (cycleEndDate < cycleStartDate) {
      return t("courseReview.invalidDates");
    } else if (
      !cycleType.id ||
      !cycleName ||
      !cycleStartDate ||
      !cycleEndDate
    ) {
      return t("cyclesAndGoals.cycleVerification");
    } else {
      return "";
    }
  }

  async function handleSubmit() {
    const errorMessage = isNotComplete();
    if (!errorMessage) {
      setSubmitting(true);
      if (cycle?.id) {
        await handleUpdateCycle();
      } else {
        await handleCreateCycle();
      }
      setSubmitting(false);
    } else {
      showNotification(errorMessage, ERROR);
    }
  }

  function handleCycleTypeSelect(typeId) {
    const selectedType = cycleTypes.find((type) => type.id === typeId);
    setCycleType(selectedType);
  }

  function handleParentCycleSelect(cycleId) {
    const selectedCycle = cycles.find((cycle) => cycle.id === cycleId);
    setParentCycle(selectedCycle || noneOption);
  }

  const handleClose = () => {
    setCycleName(cycle?.name || "");
    setParentCycle(cycle?.parent_cycle || {});
    setCycleType(cycle?.type || {});
    setCycleStartDate(cycle?.start_date || "");
    setCycleEndDate(cycle?.end_date || "");
    setGoals(cycle?.goals || []);
  };

  return (
    <>
      <Modal
        open={open}
        onExited={handleClose}
        size="large"
        label={t("cyclesAndGoals.cycleType.configure")}
      >
        <Modal.Header>
          <Grid hAlign="space-between">
            <Grid.Row>
              <Grid.Col>
                <Heading level="h2" as="h1">
                  {t("cyclesAndGoals.configure")}
                </Heading>
              </Grid.Col>
              <Grid.Col width="auto">
                <CloseButton
                  size="small"
                  screenReaderLabel={t("global.close")}
                  onClick={() => setOpen(false)}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Modal.Header>
        <Modal.Body>
          <Grid colSpacing="small" rowSpacing="small" vAlign="middle">
            <Grid.Row>
              <Grid.Col width={6}>
                <TextInput
                  value={cycleName}
                  id="cycle-name"
                  renderLabel={
                    <Text size="small">{t("cyclesAndGoals.name")}</Text>
                  }
                  onChange={(e) => setCycleName(e.target.value)}
                />
              </Grid.Col>
              <Grid.Col width={6}>
                <Selector
                  id="parent-cycle-selector"
                  label={
                    <Text size="small">
                      {t("cyclesAndGoals.parentCycleSelect")}
                    </Text>
                  }
                  assistiveText={t("cyclesAndGoals.parentCycleSelectAssistive")}
                  selectHandler={handleParentCycleSelect}
                  options={[
                    noneOption,
                    ...cycles.filter((c) => c.id !== cycle?.id),
                  ]}
                  selectedItemId={parentCycle.id}
                />
              </Grid.Col>
            </Grid.Row>
            <Grid.Row>
              <Grid.Col width={8}>
                <Selector
                  id="cycle-type-selector"
                  label={
                    <Text size="small">
                      {t("cyclesAndGoals.typeSelect.label")}
                    </Text>
                  }
                  assistiveText={t("cyclesAndGoals.typeSelect.assistiveText")}
                  selectHandler={handleCycleTypeSelect}
                  options={cycleTypes}
                  selectedItemId={cycleType.id}
                />
              </Grid.Col>
              <Grid.Col vAlign="bottom">
                <Button
                  id="manage-cycle-types-modal-button"
                  color="primary"
                  onClick={() => setCycleTypePanelOpen(true)}
                >
                  {t("cyclesAndGoals.cycleType.manageCycleTypes")}
                </Button>
              </Grid.Col>
            </Grid.Row>
            <Grid.Row>
              <Grid.Col>
                <InputDate
                  id="date-1-selector"
                  label={
                    <Text size="small">{t("cyclesAndGoals.startDate")}</Text>
                  }
                  selectHandler={(date) => setCycleStartDate(date)}
                  selectedDate={cycleStartDate}
                />
              </Grid.Col>
              <Grid.Col>
                <InputDate
                  id="date-2-selector"
                  label={
                    <Text size="small">{t("cyclesAndGoals.endDate")}</Text>
                  }
                  selectHandler={(date) => setCycleEndDate(date)}
                  selectedDate={cycleEndDate}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
          <div style={{ marginTop: 15 }}>
            <Heading as="h4" level="h4">
              <Text size="small">
                {t("cyclesAndGoals.cycleGoal.goalsAndObjectives")}
              </Text>
            </Heading>
            <div style={{ marginTop: 15 }}>
              <List isUnstyled style={{ Top: 15 }}>
                {goals.map((goal) => (
                  <List.Item key={goal.id}>
                    <GoalEntry goal={goal} setGoals={setGoals} />
                  </List.Item>
                ))}
              </List>
              <div style={{ float: "right" }}>
                <Button
                  id="add-cycle-goal-button"
                  color="primary"
                  renderIcon={IconAddLine}
                  onClick={() => setGoalPanelOpen(true)}
                >
                  {t("cyclesAndGoals.cycleGoal.addCycleGoalButton")}
                </Button>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button margin="0 small 0 0" onClick={() => setOpen(false)}>
            {t("global.cancel")}
          </Button>
          <Button
            color="primary"
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              t("global.submit")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <CycleTypePanel
        open={cycleTypePanelOpen}
        setOpen={setCycleTypePanelOpen}
      />
      <CycleGoalEditModal
        open={goalPanelOpen}
        setOpen={setGoalPanelOpen}
        setGoals={setGoals}
      />
    </>
  );
};
